import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Icons } from "@/components/ui";
import type { FrameData } from "@/lib/frame-data";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import {
  useFrameChromeConfig,
  type FrameChromeConfig,
} from "../shared/frame-chrome-config";
import { useFrameChromeHidden } from "../shared/frame-renderer-context";
import { fileCardShape, pdfTitle } from "./file-card-shape";
import type { PdfReadError } from "./pdf-fetch";
import { PdfPreviewCover } from "./pdf-preview-cover";
import { useRevealLabel, type FileActions } from "./use-file-actions";
import {
  closePdfDocument,
  forgetPdfFailure,
  openPdfDocument,
  pdfPageRatio,
  releaseCanvas,
  renderPdfPage,
  type PdfDocument,
} from "./pdf-pages";

/** A page is drawn this far before it scrolls into the pane's view. */
const NEAR_VIEW_MARGIN = "800px 0px";
/** A resize re-draws the pages once it settles. */
const RESIZE_SETTLE_MS = 150;

interface FilePdfViewProps {
  data: FrameData;
  name: string;
  /** What the file offers here, and where pdf.js reads it (`pdf`). */
  actions: FileActions;
}

/**
 * One page: blank paper of the first page's ratio until it nears the pane's
 * view, then drawn by pdf.js at the window's width; its canvas goes again
 * when it scrolls far away, so a long document keeps a few only.
 */
function PdfPage({
  pdf,
  pageNumber,
  ratio,
  width,
  near,
}: {
  pdf: PdfDocument;
  pageNumber: number;
  ratio: number;
  width: number;
  near: boolean;
}) {
  const t = useT(framesFileMessages);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!near || !canvas || width <= 0) return;
    let cancelled = false;
    let cancel: (() => void) | null = null;
    void renderPdfPage(pdf, pageNumber, canvas, width, {
      pixelRatio: window.devicePixelRatio,
    })
      .then(
        (task) => {
          if (cancelled) task.cancel();
          else cancel = () => task.cancel();
          return task.promise;
        },
        () => undefined,
      )
      .catch(() => undefined);
    return () => {
      cancelled = true;
      cancel?.();
      releaseCanvas(canvas);
    };
  }, [near, pageNumber, pdf, width]);

  return (
    <div
      className="w-full overflow-hidden bg-[var(--card-paper)]"
      style={{ aspectRatio: ratio }}
      data-testid={`file-frame-pdf-page-${pageNumber}`}
      data-page={pageNumber}
    >
      {near && (
        <canvas
          ref={canvasRef}
          className="block h-full w-full object-contain"
          aria-label={t("pageNumber", { number: pageNumber })}
        />
      )}
    </div>
  );
}

/**
 * The PDF itself, drawn by pdf.js on both platforms, as its card is: the
 * pages edge to edge at the window's width (the window has the page's
 * ratio), one under the other with a hairline between, each drawn as it
 * nears the view. The name and Open show in the window's hover pill, or in
 * a small pill of its own on hover where no window hosts them (with Show
 * in Finder or Explorer for a file linked here). Until the pages are drawn,
 * and when the document cannot be read, the window shows the wall card's
 * cover: a spinner while loading, else why with what helps and Try again;
 * a file linked on another computer shows its cover and where it is.
 */
export function FilePdfView({ data, name, actions }: FilePdfViewProps) {
  const tc = useT(commonMessages);
  const revealLabel = useRevealLabel();
  const { pdf: source, onOpen, onReveal } = actions;
  const shape = useMemo(() => fileCardShape(data), [data]);
  const thumbnailUrl = shape.kind === "pdf" ? shape.url : "";
  const [pdf, setPdf] = useState<PdfDocument | null>(null);
  // Without a source there is nothing pdf.js may read: a web URL that is
  // not https (or not public) is refused, no URL at all is a failure; a
  // file elsewhere, or this computer not known yet, is no error.
  const [error, setError] = useState<PdfReadError | null>(
    source || actions.pending || actions.notice
      ? null
      : onOpen
        ? "refused"
        : "failed",
  );
  const [attempt, setAttempt] = useState(0);
  const [ratio, setRatio] = useState(shape.ratio);
  const [width, setWidth] = useState(0);
  const [nearPages, setNearPages] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const pagesRef = useRef<HTMLDivElement>(null);
  const label = name || "PDF";
  const chromeConfig = useMemo<FrameChromeConfig>(
    () => ({
      tools: [
        { kind: "label", id: "file-name", label },
        ...(onOpen
          ? [
              {
                kind: "button" as const,
                id: "file-open",
                label: tc("open"),
                onSelect: onOpen,
              },
            ]
          : []),
        ...(onReveal
          ? [
              {
                kind: "button" as const,
                id: "file-reveal",
                label: revealLabel,
                onSelect: onReveal,
              },
            ]
          : []),
      ],
    }),
    [label, onOpen, onReveal, revealLabel, tc],
  );
  const toolsInPill = useFrameChromeConfig(chromeConfig);
  const chromeHidden = useFrameChromeHidden();

  useEffect(() => {
    if (!source) return;
    const controller = new AbortController();
    let opened: PdfDocument | null = null;
    void (async () => {
      const read = await openPdfDocument(source, controller.signal);
      if (controller.signal.aborted) {
        if ("pdf" in read) closePdfDocument(read.pdf);
        return;
      }
      if ("error" in read) {
        setError(read.error);
        return;
      }
      opened = read.pdf;
      const pageRatio = await pdfPageRatio(read.pdf, 1).catch(() => null);
      if (controller.signal.aborted) return;
      if (pageRatio) setRatio(pageRatio);
      setPdf(read.pdf);
    })();
    return () => {
      controller.abort();
      closePdfDocument(opened);
    };
  }, [attempt, source]);

  const retry = () => {
    if (!source) return;
    forgetPdfFailure(source);
    setError(null);
    setAttempt((count) => count + 1);
  };

  useEffect(() => {
    const pages = pagesRef.current;
    if (!pages) return;
    let timer: number | undefined;
    const measure = () => setWidth(pages.clientWidth);
    measure();
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, RESIZE_SETTLE_MS);
    });
    observer.observe(pages);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  // One observer on the pane's own scroller marks the pages near its view.
  useEffect(() => {
    const pane = pagesRef.current;
    if (!pane || !pdf) return;
    const observer = new IntersectionObserver(
      (entries) =>
        setNearPages((previous) => {
          const next = new Set(previous);
          for (const entry of entries) {
            const page = Number((entry.target as HTMLElement).dataset.page);
            if (entry.isIntersecting) next.add(page);
            else next.delete(page);
          }
          return next;
        }),
      { root: pane, rootMargin: NEAR_VIEW_MARGIN },
    );
    for (const sheet of pane.querySelectorAll("[data-page]")) {
      observer.observe(sheet);
    }
    return () => observer.disconnect();
  }, [pdf]);

  return (
    <div className="group/pdf relative flex min-h-0 flex-1 flex-col">
      <div
        ref={pagesRef}
        className="nodrag min-h-0 flex-1 overflow-y-auto"
        data-frame-content="true"
        data-testid="file-frame-pdf"
      >
        {pdf ? (
          // One paper filter dims every page on ink themes, as on the card.
          <div className="flex flex-col gap-px bg-card-line [filter:var(--card-paper-filter)]">
            {Array.from({ length: pdf.numPages }, (_, index) => (
              <PdfPage
                key={index}
                pdf={pdf}
                pageNumber={index + 1}
                ratio={ratio}
                width={width}
                near={nearPages.has(index + 1)}
              />
            ))}
          </div>
        ) : (
          <PdfPreviewCover
            thumbnailUrl={thumbnailUrl}
            title={pdfTitle(data)}
            ratio={ratio}
            width={width}
            error={error}
            actions={actions}
            onRetry={retry}
          />
        )}
      </div>
      {!toolsInPill && !chromeHidden && (
        <div
          className="pointer-events-none absolute right-2 top-2 flex max-w-[calc(100%-1rem)] items-center gap-1 rounded-pill bg-[var(--card-chrome)] py-1 pl-3 pr-1 text-card-ink opacity-0 shadow-[var(--card-chrome-shadow)] transition-opacity duration-150 focus-within:pointer-events-auto focus-within:opacity-100 group-hover/pdf:pointer-events-auto group-hover/pdf:opacity-100"
          data-frame-drag-handle="true"
          data-testid="file-frame-pdf-pill"
        >
          <span
            className="min-w-0 truncate text-appkit-caption font-medium"
            data-testid="file-frame-pdf-name"
          >
            {label}
          </span>
          {onOpen && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="nodrag shrink-0 rounded-pill focus-visible:ring-2 focus-visible:ring-accent"
              onClick={onOpen}
              data-testid="file-frame-open"
            >
              <Icons.ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {tc("open")}
            </Button>
          )}
          {onReveal && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="nodrag shrink-0 rounded-pill focus-visible:ring-2 focus-visible:ring-accent"
              aria-label={revealLabel}
              title={revealLabel}
              onClick={onReveal}
              data-testid="file-frame-reveal"
            >
              <Icons.FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
